/* ============================================================== */
/*                                                                */
/*   printEmrV4  ·  helper เปิดหน้าต่างพิมพ์ของ HIS V4 (browser เท่านั้น) */
/*                                                                */
/*   แบบ 4d ใช้ iframe ลับ + window.print():                        */
/*     docs/design/PatientHistory.html (printEmr / printDoc)        */
/*   HIS V3 ใช้ getPdfMake (apps/HISV3/PHV/PHVInterface.ts:26)       */
/*   -> V4 คงท่าของแบบ (iframe) เพราะง่ายกว่าและทดสอบง่าย           */
/*                                                                */
/*   ไม่ใช่ domain: ไม่มี import API/SDK และ jsdom จะคืน false      */
/*                                                                */
/* ============================================================== */

/** รูปที่ไม่ตอบ load/error ภายในเวลานี้ถือว่าโหลดไม่สำเร็จ */
export const PRINT_IMAGE_TIMEOUT_MS = 15000;

export type PrintOptions = {
  onError?: () => void;
  /** มีรูปที่โหลดไม่สำเร็จ แต่ยังพิมพ์เอกสารต่อโดยแทนรูปด้วยข้อความ */
  onMissingImages?: (count: number) => void;
  /** เอกสารที่เนื้อหาคือรูป: รูปโหลดไม่ได้ต้องไม่สั่งพิมพ์ */
  requireImages?: boolean;
};

const waitForImage = (img: HTMLImageElement): Promise<boolean> => {
  if (img.complete) return Promise.resolve(img.naturalWidth > 0);
  return new Promise<boolean>((resolve) => {
    const timer = setTimeout(() => resolve(false), PRINT_IMAGE_TIMEOUT_MS);
    const done = (loaded: boolean): void => {
      clearTimeout(timer);
      resolve(loaded);
    };
    img.onload = () => done(true);
    img.onerror = () => done(false);
  });
};

/** ส่ง HTML ไปยังเครื่องพิมพ์จริง (บันทึกเป็น PDF ที่ปลายทาง) — false เมื่อทำไม่ได้ */
export const printHtmlDocument = (html: string, options: PrintOptions = {}): boolean => {
  try {
    if (typeof document === "undefined" || !document.body) return false;

    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    document.body.appendChild(frame);

    const target = frame.contentWindow;
    if (target === null || target.document === undefined) {
      document.body.removeChild(frame);
      return false;
    }

    target.document.open();
    target.document.write(html);
    target.document.close();

    const images = Array.from(target.document.images);
    Promise.all(images.map(waitForImage)).then((loaded) => {
      const missing = images.filter((_, index) => !loaded[index]);
      if (missing.length > 0 && options.requireImages) {
        options.onError?.();
        frame.remove();
        return;
      }
      if (missing.length > 0) {
        missing.forEach((img) => {
          const note = target.document.createElement("div");
          note.textContent = "โหลดรูปไม่สำเร็จ";
          img.replaceWith(note);
        });
        options.onMissingImages?.(missing.length);
      }
      try {
        target.focus();
        target.print();
      } catch {
        options.onError?.();
      }
      setTimeout(() => {
        try {
          document.body.removeChild(frame);
        } catch {
          /* removes แล้ว */
        }
      }, 1500);
    });

    return true;
  } catch {
    return false;
  }
};
